import message from "@/utils/globalMessage";
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Input, Tag, Checkbox } from "antd";
import ResponsiveModal from "@components/ResponsiveModal";
import Table from "@components/ResponsiveTable";
import { EditOutlined, PlusOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { connect } from "react-redux";
import { api } from "@redux/api";
import { getAllPermissionKeys, PERMISSION_VALUES } from './utils';
import { permissionTree } from '@config/permissionTree';
import _ from "lodash";
import Auth from '@/components/Auth';
import Select from "@components/ResponsiveSelect";

import DeleteConfirm from "@components/Feedback/DeleteConfirm";
const { Option } = Select;

const Role = ({ role: userRole }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false); // 新增查看弹窗状态
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null); // 新增查看记录状态
  const [permissionValues, setPermissionValues] = useState({}); // 存储每个权限key对应的值 {key: 0|1|3}
  const [viewPermissionValues, setViewPermissionValues] = useState({}); // 查看模式的权限值
  const [form] = Form.useForm();

  // 角色映射配置
  const roleConfig = {
    1: { label: 'Super User', color: 'red' },
    2.1: { label: 'Account Manager', color: 'orange' },
    2.2: { label: 'Portfolio Manager', color: 'blue' },
    3: { label: 'Company Admin', color: 'green' },
    4: { label: 'Standard User', color: 'default' },
  };

  // 表格列定义
  const columns = [
    {
      title: 'Profile Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <Auth code="profile" level={1} fallback={text}>
          <Button
            type="link"
            style={{
              padding: 0,
              height: 'auto',
              color: '#1890ff',
              fontWeight: 'normal'
            }}
            onClick={() => handleView(record)}
          >
            {text}
          </Button>
        </Auth>

      ),
    },
    {
      title: 'Account Level',
      dataIndex: 'role',
      key: 'role',
      width: 150,
      render: (roleValue) => {
        const config = roleConfig[roleValue];
        if (config) {
          return (
            <Tag color={config.color}>
              {config.label}
            </Tag>
          );
        }
        return <Tag>{roleValue}</Tag>;
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdby_name',
      key: 'createdby_name',
      width: 120,
    },
    {
      title: 'Modified By',
      dataIndex: 'lastModified_name',
      key: 'lastModified_name',
      width: 120,
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Auth code="profile" level={2}>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={userRole !== '1.0'}
            >
              Edit
            </Button>
          </Auth>
          <Auth code="profile" level={3}>
            <DeleteConfirm
              title="Are you sure you want to delete this role?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </DeleteConfirm>
          </Auth>
        </div>
      ),
    },
  ];

  // 获取数据
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.filterProfile({});
      setData(response.data.results || []);
    } catch (error) {
      message.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    fetchData();
  }, []);

  // 将API数据转换为权限值映射
  const convertDataToPermissionValues = (record) => {
    const values = {};
    const allPermissionKeys = getAllPermissionKeys();

    allPermissionKeys.forEach(key => {
      const value = record[key];
      if (value !== undefined && value !== null) {
        values[key] = value;
      } else {
        values[key] = PERMISSION_VALUES.DISABLED;
      }
    });

    return values;
  };

  // 将权限值映射转换为API数据格式
  const convertPermissionValuesToData = (values) => {
    const result = {};
    const allPermissionKeys = getAllPermissionKeys();

    allPermissionKeys.forEach(key => {
      result[key] = values[key] !== undefined ? values[key] : PERMISSION_VALUES.DISABLED;
    });

    return result;
  };

  // 打开查看弹窗
  const handleView = (record) => {
    setViewingRecord(record);
    setViewModalVisible(true);

    // 设置权限值（只读模式）
    const initialValues = convertDataToPermissionValues(record);
    setViewPermissionValues(initialValues);
  };

  // 打开编辑弹窗
  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditModalVisible(true);

    // 设置表单初始值
    form.setFieldsValue({
      name: record.name,
      role: record.role,
      comment: record.comment,
    });

    // 设置权限值
    const initialValues = convertDataToPermissionValues(record);
    setPermissionValues(initialValues);
  };

  // 创建新角色
  const handleCreate = () => {
    setEditingRecord(null);
    setEditModalVisible(true);
    form.resetFields();
    // 初始化所有权限为禁用
    const initialValues = {};
    getAllPermissionKeys().forEach(key => {
      initialValues[key] = PERMISSION_VALUES.DISABLED;
    });
    setPermissionValues(initialValues);
  };

  // 删除角色
  const handleDelete = async (record) => {
    try {
      await api.deleteUserpermissions(record.id);
      message.success('Profile deleted successfully');
      fetchData(); // 刷新数据
    } catch (error) {
      message.error('Failed to delete, this profile is used by other users');
    }
  };

  // 保存角色
  const handleSave = async () => {
    const formValues = await form.validateFields();
    const permissionData = convertPermissionValuesToData(permissionValues);
    const saveData = {
      ...formValues,
      ...permissionData,
    };

    if (editingRecord) {
      // 更新现有角色
      await api.editUserpermissions(editingRecord.id, saveData);
      message.success('Profile updated successfully');
    } else {
      // 创建新角色
      await api.createUserpermissions(saveData);
      message.success('Profile created successfully');
    }

    setEditModalVisible(false);
    fetchData(); // 刷新数据
  };

  // 关闭编辑弹窗
  const handleCancel = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
    setPermissionValues({});
  };

  // 关闭查看弹窗
  const handleViewCancel = () => {
    setViewModalVisible(false);
    setViewingRecord(null);
    setViewPermissionValues({});
  };

  // 处理权限checkbox变化
  const handlePermissionChange = (key, value) => {
    setPermissionValues(prev => ({
      ...prev,
      [key]: prev[key] === value ? PERMISSION_VALUES.DISABLED : value
    }));
  };

  // 将permissionTree转换为Table数据源（扁平化带层级）
  const convertTreeToTableData = (treeData, level = 0) => {
    const result = [];
    treeData.forEach(node => {
      result.push({
        key: node.key,
        title: node.title,
        level,
        permissions: node.permissions,
        children: node.children && node.children.length > 0
          ? convertTreeToTableData(node.children, level + 1)
          : undefined
      });
    });
    return result;
  };

  // 获取所有可展开的行keys
  const getAllExpandableRowKeys = (treeData) => {
    const keys = [];
    const traverse = (nodes) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          keys.push(node.key);
          traverse(node.children);
        }
      });
    };
    traverse(treeData);
    return keys;
  };

  // 权限表格数据源
  const permissionTableData = useMemo(() => convertTreeToTableData(permissionTree), []);

  // 默认展开所有行的keys
  const defaultExpandedRowKeys = useMemo(() => getAllExpandableRowKeys(permissionTree), []);

  // 权限TreeTable列定义
  const getPermissionColumns = (values, disabled = false, onChange = null) => [
    {
      title: 'Permission',
      dataIndex: 'title',
      key: 'title',
      width: '40%',
    },
    {
      title: 'Read Only',
      key: 'readonly',
      width: '20%',
      align: 'center',
      render: (_, record) => {

        return record.permissions.includes(1) ? (
          <Checkbox
            checked={values[record.key] === PERMISSION_VALUES.ENABLED}
            disabled={disabled}
            onChange={() => onChange && onChange(record.key, PERMISSION_VALUES.ENABLED)}
          />
        ) : null
      },
    },
    {
      title: 'Write',
      key: 'write',
      width: '20%',
      align: 'center',
      render: (_, record) => {

        return record.permissions.includes(2) ? (
          <Checkbox
            checked={values[record.key] === PERMISSION_VALUES.WRITE_ACCESS}
            disabled={disabled}
            onChange={() => onChange && onChange(record.key, PERMISSION_VALUES.WRITE_ACCESS)}
          />
        ) : null
      }
    },
    {
      title: 'Full Access',
      key: 'fullaccess',
      width: '20%',
      align: 'center',
      render: (_, record) => {

        return record.permissions.includes(3) ? (
          <Checkbox
            checked={values[record.key] === PERMISSION_VALUES.FULL_ACCESS}
            disabled={disabled}
            onChange={() => onChange && onChange(record.key, PERMISSION_VALUES.FULL_ACCESS)}
          />
        ) : null
      }
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      {/* 操作按钮区域 */}
      <div style={{ marginBottom: '16px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchData}
          style={{ marginRight: '8px' }}
        >
          Refresh
        </Button>
        <Auth code="profile" level={2}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            New Profile
          </Button>
        </Auth>
      </div>

      {/* 角色列表表格 */}
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`,
        }}
      />

      {/* 查看弹窗（只读模式） */}
      <ResponsiveModal
        title={`View Profile: ${viewingRecord?.name || ''}`}
        open={viewModalVisible}
        onCancel={handleViewCancel}
        width={800}
        footer={[
          <Button key="close" onClick={handleViewCancel}>
            Close
          </Button>
        ]}
      >
        {viewingRecord && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: 'bold', color: '#666' }}>Profile Name:</label>
              <div style={{ marginTop: '4px', fontSize: '14px' }}>{viewingRecord.name}</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: 'bold', color: '#666' }}>Account Level:</label>
              <div style={{ marginTop: '4px' }}>
                {roleConfig[viewingRecord.role] ? (
                  <Tag color={roleConfig[viewingRecord.role].color}>
                    {roleConfig[viewingRecord.role].label}
                  </Tag>
                ) : (
                  <Tag>{viewingRecord.role}</Tag>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: 'bold', color: '#666' }}>Comment:</label>
              <div style={{ marginTop: '4px', fontSize: '14px' }}>
                {viewingRecord.comment || 'No comment'}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 'bold', color: '#666' }}>Permissions:</label>
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                marginTop: '8px',
                backgroundColor: '#f9f9f9'
              }}>
                <Table
                  columns={getPermissionColumns(viewPermissionValues, true)}
                  dataSource={permissionTableData}
                  pagination={false}
                  size="small"
                  defaultExpandAllRows
                  expandable={{
                    defaultExpandedRowKeys: defaultExpandedRowKeys,
                  }}
                  rowKey="key"
                />
              </div>
            </div>
          </div>
        )}
      </ResponsiveModal>

      {/* 编辑/创建弹窗 */}
      <ResponsiveModal
        title={editingRecord ? 'Edit Profile' : 'Create New Profile'}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={handleCancel}
        width={800}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: '20px' }}
                        autoComplete="off">
          <Form.Item
            name="name"
            label="Profile Name"
            rules={[{ required: true, message: 'Please enter role name' }]}
          >
            <Input placeholder="Enter role name" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Account Level"
            rules={[{ required: true, message: 'Please select role level' }]}
          >
            <Select placeholder="Select role level">
              <Option value={1}>Super User</Option>
              <Option value={2.1}>Account Manager</Option>
              <Option value={2.2}>Portfolio Manager</Option>
              <Option value={3}>Company Admin</Option>
              <Option value={4}>Standard User</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="comment"
            label="Comment"
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter comment (optional)"
            />
          </Form.Item>

          <Form.Item label="Permissions">
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}>
              <Table
                columns={getPermissionColumns(permissionValues, false, handlePermissionChange)}
                dataSource={permissionTableData}
                pagination={false}
                size="small"
                defaultExpandAllRows
                expandable={{
                  defaultExpandedRowKeys: defaultExpandedRowKeys,
                }}
                rowKey="key"
              />
            </div>
          </Form.Item>
        </Form>
      </ResponsiveModal>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    role: _.get(state, 'Auth.role') ? Object.keys(_.get(state, 'Auth.role'))[0] : null,
  }
}

export default connect(mapStateToProps)(Role);
